import React from "react";

import { connect } from "react-redux";

import CalendarModal from "@app/components/search-form-hydrated/calendar-modal";
import PassengersModal from "@app/components/search-form-hydrated/passengers-modal";
import { SeatSelectionV2Dialog } from "@app/modules/checkout/components/seat-selection-v2/seat-selection-dialog";
import type { CheckoutReduxState } from "@app/types/checkout-redux-types";
import type { ModalType } from "@app/types/modal";
import type { ResultsReduxState } from "@app/types/results-redux-types";

import { Authentication } from "./authentication";
import { CancelTrip } from "./cancel-trip";
import { CancellationStatusInfo } from "./cancel-trip/cancellation-status-info";
import { CartCreation } from "./cart-creation";
import { CheckoutErrorModal } from "./checkout-error/checkout-error";
import { CheckoutPriceChanged } from "./checkout-price-changed";
import { CreditsActivity } from "./credits-activity";
import { DeleteBillingAddress } from "./delete-billing-address";
import { DeleteCreditCard } from "./delete-credit-card";
import { DeletePassenger } from "./delete-passenger";
import { DepartureChanged } from "./departure-changed";
import { DownloadTickets } from "./download-tickets";
import { DownloadTicketsWithBundle } from "./download-tickets-with-bundle";
import { EmailCollection } from "./email-collection";
import { ErrorMessage } from "./error-message";
import HelpCenterRedirect from "./help-center-redirect/help-center-redirect";
import { HepstarTermsAndConditions } from "./hepstar-terms-conditions";
import { PriceChanged } from "./price-changed";
import { ProcessingPayment } from "./processing-payment";
import { PromptPassword } from "./prompt-password";
import { RefundTerms } from "./refund-terms";
import { ResendConfirmationEmail } from "./resend-confirmation-email";
import { TTLWarning } from "./ttl-warning";

interface OwnProps {}

interface ReduxProps {
  modal: ModalType | null;
}

const ModalManager: React.FC<ReduxProps & OwnProps> = props => {
  const { modal } = props;

  if (!modal) {
    return null;
  }

  switch (modal.type) {
    case "ttl_warning":
      return <TTLWarning />;
    case "error_message":
      return (
        <ErrorMessage
          error={modal.error}
          context_id={modal.context}
          onAction={modal.onAction}
          is_async_cart={modal.is_async_cart}
        />
      );
    case "email_collection":
      return <EmailCollection />;
    case "passengers_selector":
      return <PassengersModal input_element={modal.input_element} />;
    case "calendar":
      return (
        <CalendarModal
          selected_date={modal.selected_date}
          direction={modal.direction}
          input_element={modal.input_element}
        />
      );
    case "cart_creation":
      return <CartCreation />;
    case "price_changed":
      return (
        <PriceChanged
          id={modal.id}
          cart={modal.cart}
          onAccept={modal.onAccept}
          is_async_cart={modal.is_async_cart}
        />
      );
    case "departure_changed":
      return (
        <DepartureChanged error={modal.error} onDismiss={modal.onDismiss} />
      );
    case "processing_payment":
      return <ProcessingPayment />;
    case "checkout_price_changed":
      return (
        <CheckoutPriceChanged
          refreshed_cart_charges={modal.refreshed_cart_charges}
          passenger_section_state={modal.passenger_section_state}
          passenger_section_fields={modal.passenger_section_fields}
          onAccept={modal.onAccept}
          onDecline={modal.onDecline}
        />
      );
    case "checkout_error":
      return (
        <CheckoutErrorModal
          error={modal.error}
          checkout_step={modal.checkout_step}
          onPassengerSectionSubmit={modal.onPassengerSectionSubmit}
          onDismiss={modal.onDismiss}
        />
      );
    case "authentication":
      return (
        <Authentication
          opening_view={modal.opening_view}
          redirect_url={modal.redirect_url}
        />
      );
    case "delete_passenger":
      return <DeletePassenger onConfirm={modal.onConfirm} />;
    case "download_tickets":
      return <DownloadTickets tickets={modal.tickets} />;
    case "download_tickets_with_bundle":
      return <DownloadTicketsWithBundle purchase={modal.purchase} />;
    case "prompt_password":
      return (
        <PromptPassword
          onEmailUpdated={modal.onEmailUpdated}
          updated_email={modal.updated_email}
          onClosed={modal.onClosed}
        />
      );
    case "cancel_trip":
      return (
        <CancelTrip
          purchase={modal.purchase}
          cancellation_options={modal.cancellation_options}
          updatePurchaseState={modal.updatePurchaseState}
        />
      );
    case "not_refundable_anymore":
      return <CancellationStatusInfo status="not_refundable_anymore" />;
    case "purchase_already_cancelled":
      return <CancellationStatusInfo status="already_cancelled" />;
    case "refund_terms":
      return <RefundTerms terms={modal.terms} trip_type={modal.trip_type} />;
    case "view_credits_activity":
      return <CreditsActivity />;
    case "delete_credit_card":
      return <DeleteCreditCard onConfirm={modal.onConfirm} />;
    case "delete_billing_address":
      return <DeleteBillingAddress onConfirm={modal.onConfirm} />;
    case "hepstar_terms_and_conditions":
      return (
        <HepstarTermsAndConditions
          onClose={modal.onClose}
          cards_closed={modal.cards_closed}
        />
      );
    case "cart_seat_selection":
      return (
        <SeatSelectionV2Dialog
          onClose={modal.onClose}
          direction={modal.direction}
          passenger_index={modal.passenger_index}
          onGenderSelect={modal.onGenderSelect}
        />
      );
    case "resend_confirmation_email":
      return (
        <ResendConfirmationEmail
          user_email={modal.user_email}
          sendEmail={modal.sendEmail}
          onEmailSent={modal.onEmailSent}
        />
      );
    case "help_center_redirect":
      return <HelpCenterRedirect />;
    default:
      return null;
  }
};

const mapStateToProps = (
  state: ResultsReduxState | CheckoutReduxState
): any => {
  const modal = state.modals[state.modals.length - 1] || null;

  return { modal };
};

const ModalManagerComponent = connect<
  ReduxProps,
  {},
  OwnProps,
  ResultsReduxState
>(mapStateToProps)(ModalManager);

export { ModalManagerComponent as ModalManager };
